'use client'

import type { SourceData } from '@/components/ui/source-accordion'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { AvatarGroup } from '@/components/ui/avatar'
import { MetricValue } from '@/components/ui/metrics'
import { SourceList } from '@/components/ui/source-accordion'
import { SourceIcon, sourceKey } from '@/components/icons/source-icons'
import { AreaPlot, LinePlot, StackedBarPlot } from '@/components/charts/chart-plots'

import {
    ChartCard,
    ChartCardFooter,
    ChartCardHeader,
    LegendItem,
    StackedTrack
} from '@/components/charts/chart-parts'

import {
    ACQUISITION_CHANNELS,
    ADVOCATE_GROWTH,
    REVENUE_BANDS,
    REVENUE_DATA,
    REVENUE_KEYS,
    SUBSCRIBER_LINES,
    SUBSCRIBER_TRENDS
} from '@/constants/chart-data'

/*
 * GoodUnited / Chart content blocks. Figma: 21 / Blocks / Chart content and 12 / Charts / Chart compositions.
 * Ready-made chart cards built from the shared chart anatomy. Swap the sample data for real data
 * and keep the anatomy: header, metrics, plot, legend, footer.
 */

interface CompactChartProps {
    variant?: 'area' | 'line'
    className?: string
}

/* Chart/Area/Compact and Chart/Line/Compact: one segment's growth with its people. */
export function ChartCompact({ variant = 'area', className }: CompactChartProps) {
    return (
        <ChartCard className={cn('w-full max-w-sm', className)}>
            <div className='flex flex-col gap-1'>
                <h3 className='font-heading text-lg font-semibold text-text-primary'>
                    Pro-Life Advocate
                </h3>
                <div className='flex items-center justify-between gap-4'>
                    <span className='font-heading text-3xl font-semibold text-text-primary'>
                        9,420
                    </span>
                    <AvatarGroup count={5} size='sm' label='Five of the people in this segment' />
                </div>
            </div>
            <AreaPlot data={ADVOCATE_GROWTH} xKey='day' yKey='value' label='Supporters' variant={variant} height={120} />
            <ChartCardFooter summary='712 new · 36% of base' period='Aug 1 – Today · range 8.4k–9.6k' />
        </ChartCard>
    )
}

/* Chart/Line/Multiple: Subscriber trends with a shaded total and one line per channel. */
export function ChartSubscriberTrends({ className }: { className?: string }) {
    const [hidden, setHidden] = useState<string[]>([])

    const toggle = (key: string) => setHidden(current => current.includes(key) ? current.filter(item => item !== key) : [...current, key])

    return (
        <ChartCard className={className}>
            <ChartCardHeader title='Subscriber trends' description='Monthly activity · January–June 2026 · values shown for June' />
            <div className='flex flex-wrap gap-x-12 gap-y-4'>
                <MetricValue label='Total subscribers' value='11,000' />
                <MetricValue label='Unsubscribes' value='500' />
            </div>
            <LinePlot
                data={SUBSCRIBER_TRENDS}
                xKey='month'
                lines={SUBSCRIBER_LINES}
                shadedKey='total'
                hidden={hidden}
                domain={[-2000, 12000]}
                ticks={[-2000, 0, 2000, 4000, 6000, 8000, 10000, 12000]}
                height={320}
            />
            <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6'>
                {SUBSCRIBER_LINES.map(line => (
                    <LegendItem
                        key={line.key}
                        source={sourceKey(line.key)}
                        series={line.series}
                        label={line.label}
                        value={line.value}
                        visible={!hidden.includes(line.key)}
                        onToggle={() => toggle(line.key)}
                    />
                ))}
            </div>
            <ChartCardFooter
                summary='Unsubscribes are plotted below zero.'
                period='June values shown. Total subscribers is a separate cumulative metric; it is not the sum of the activity series.'
            />
        </ChartCard>
    )
}

const channelGroups: Record<string, SourceData['groups']> = {
    facebook: [
        { id: 'facebook-giving-tuesday', label: 'Giving Tuesday post', supporters: { value: '64', detail: '53%' } },
        { id: 'facebook-birthday', label: 'Birthday fundraiser prompt', supporters: { value: '56', detail: '47%' } }
    ],
    messenger: [
        { id: 'messenger-lookalike', label: 'Lookalike audience', supporters: { value: '48', detail: '60%' } },
        { id: 'messenger-retargeting', label: 'Retargeting', supporters: { value: '32', detail: '40%' } }
    ],
    platform: [
        { id: 'platform-grow-page', label: 'Grow page', supporters: { value: '30', detail: '60%' } },
        { id: 'platform-signup', label: 'Signup form', supporters: { value: '20', detail: '40%' } }
    ],
    challenges: [
        { id: 'challenges-mile-run', label: '100 Mile Run', supporters: { value: '62', detail: '62%' } },
        { id: 'challenges-push-ups', label: '50 Push ups', supporters: { value: '38', detail: '38%' } }
    ]
}

/* Chart/Bar/StackedHorizontal: Acquisition channels as one normalized track and an expandable source list. */
export function ChartAcquisitionChannels({ className }: { className?: string }) {
    const sources: SourceData[] = ACQUISITION_CHANNELS.map(channel => ({
        id: channel.id,
        label: channel.label,
        accent: channel.series,
        icon: <SourceIcon source={channel.id} />,
        supporters: { value: String(channel.value), detail: channel.share },
        groups: channelGroups[channel.id]
    }))

    return (
        <ChartCard className={cn('w-full max-w-md', className)}>
            <ChartCardHeader title='Acquisition channels' description='New supporters · January 2026' />
            <StackedTrack
                segments={ACQUISITION_CHANNELS.map(channel => ({
                    series: channel.series,
                    value: channel.value,
                    label: channel.label,
                    icon: <SourceIcon source={channel.id} />
                }))}
            />
            <SourceList sources={sources} />
            <ChartCardFooter summary='350 supporters across four channels' period='Each segment shows its share of the same total.' />
        </ChartCard>
    )
}

const usd = (thousands: number) => `$${(thousands * 1000).toLocaleString('en-US')}`

/* Chart/Bar/Revenue: monthly revenue by source, campaign bands and a toggleable legend. */
export function ChartRevenueOverview({ className }: { className?: string }) {
    const [hidden, setHidden] = useState<string[]>([])

    const totals = REVENUE_KEYS.map(item => ({
        ...item,
        total: REVENUE_DATA.reduce((sum, datum) => sum + (datum.values[item.key] ?? 0), 0)
    }))
    const grandTotal = totals.reduce((sum, item) => sum + item.total, 0)
    const instagram = totals.find(item => item.key === 'instagram')?.total ?? 0

    const toggle = (key: string) => setHidden(current => current.includes(key) ? current.filter(item => item !== key) : [...current, key])

    return (
        <ChartCard className={className}>
            <ChartCardHeader title='Revenue overview' action='Jan – Dec 2026' />
            <div className='flex flex-wrap gap-x-12 gap-y-4'>
                <MetricValue label='Facebook revenue' value={usd(grandTotal - instagram)} />
                <MetricValue label='Instagram revenue' value={usd(instagram)} />
            </div>
            <div className='flex flex-col gap-3'>
                <p className='text-sm font-medium text-text-primary'>
                    GoodUnited outcomes
                </p>
                <StackedBarPlot data={REVENUE_DATA} keys={REVENUE_KEYS} bands={REVENUE_BANDS} hidden={hidden} />
            </div>
            <div className='flex flex-col gap-3'>
                <p className='text-sm font-medium text-text-primary'>
                    Revenue sources
                </p>
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5'>
                    {[...totals].reverse().map(item => (
                        <LegendItem
                            key={item.key}
                            source={sourceKey(item.key)}
                            series={item.series}
                            label={item.label}
                            value={usd(item.total)}
                            share={`${(item.total / grandTotal * 100).toFixed(1)}%`}
                            visible={!hidden.includes(item.key)}
                            onToggle={() => toggle(item.key)}
                        />
                    ))}
                </div>
            </div>
        </ChartCard>
    )
}
